import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import * as Sentry from "@sentry/react";
import { useApi } from "../../hooks/useApi";
import { useBrandId } from "../../hooks/useBrandId";
import { useCompanyId } from "../../hooks/useCompanyId";
import { apiErrorMessage } from "../../utils/apiError";
import { INQUIRY_QUEUE_FILTERS, INQUIRY_SCOPES, InquiryCounts, InquiryListItem, InquiryListResponse, InquiryQueueFilter, InquiryScope } from "./types";

export const LIMIT = 25;
const EMPTY_COUNTS: InquiryCounts = { needs_reply: 0, replied: 0, offer_pending: 0, mine: 0, unassigned: 0 };

export function useInquiries() {
  const { get, loading } = useApi();
  const { get: getNextPage } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const queue = INQUIRY_QUEUE_FILTERS.find((q) => q === searchParams.get('queue')) ?? 'needs_reply';
  const scope = INQUIRY_SCOPES.find((s) => s === searchParams.get('show')) ?? 'anyone';
  const [search, setSearchState] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const { companyId } = useCompanyId();
  const { brandId } = useBrandId();
  const [pageScope, setPageScope] = useState(`${companyId}:${brandId}`);
  if (pageScope !== `${companyId}:${brandId}`) {
    setPageScope(`${companyId}:${brandId}`);
    setPage(0);
  }
  const [items, setItems] = useState<InquiryListItem[]>([]);
  const view = `${queue}:${scope}`;
  const [itemsView, setItemsView] = useState(view);
  if (itemsView !== view) {
    setItemsView(view);
    setItems([]);
  }
  const [counts, setCounts] = useState<InquiryCounts>(EMPTY_COUNTS);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const listParams = (p: number) => new URLSearchParams({
    queue,
    page: String(p + 1),
    limit: String(LIMIT),
    ...(scope !== 'anyone' && { [scope]: 'true' }),
    ...(companyId && { companyIds: String(companyId) }),
    ...(brandId && { brandId: String(brandId) }),
    ...(debouncedSearch && { search: debouncedSearch }),
  }).toString();
  const params = listParams(page);
  const currentParams = useRef(params);
  const committedParams = useRef<string | null>(null);
  const nextPageLoading = useRef(false);

  useEffect(() => {
    currentParams.current = params;
    if (committedParams.current === params) {
      committedParams.current = null;
      return;
    }
    let current = true;
    get<InquiryListResponse>(`/api/paypal-inquiries?${params}`)
      .then((res) => {
        if (!current) return;
        if (page > 0 && page >= res.pagination.totalPages) {
          setPage(Math.max(res.pagination.totalPages - 1, 0));
          return;
        }
        setItems(res.inquiries);
        setCounts(res.counts);
        setTotal(res.pagination.totalItems);
        setError(null);
      })
      .catch((err) => {
        Sentry.captureException(err);
        if (current) setError(apiErrorMessage(err, "Failed to load inquiries"));
      });
    return () => { current = false; };
  }, [get, params, page, reloadKey]);

  const setFilters = useCallback((next: { queue?: InquiryQueueFilter; scope?: InquiryScope }) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next.queue) {
        if (next.queue === 'needs_reply') params.delete('queue');
        else params.set('queue', next.queue);
      }
      if (next.scope) {
        if (next.scope === 'anyone') params.delete('show');
        else params.set('show', next.scope);
      }
      return params;
    }, { replace: true });
    setPage(0);
  }, [setSearchParams]);
  const setSearch = useCallback((next: string) => { setSearchState(next); setPage(0); }, []);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const goToNextPage = async (): Promise<InquiryListItem | null> => {
    if (nextPageLoading.current) return null;
    nextPageLoading.current = true;
    const from = params;
    const nextParams = listParams(page + 1);
    try {
      const res = await getNextPage<InquiryListResponse>(`/api/paypal-inquiries?${nextParams}`);
      if (currentParams.current !== from) return null;
      setCounts(res.counts);
      setTotal(res.pagination.totalItems);
      setError(null);
      if (page + 1 >= res.pagination.totalPages) {
        reload();
        return null;
      }
      committedParams.current = nextParams;
      setItems(res.inquiries);
      setPage(page + 1);
      return res.inquiries[0] ?? null;
    } catch (err) {
      Sentry.captureException(err);
      if (currentParams.current === from) setError(apiErrorMessage(err, "Failed to load inquiries"));
      return null;
    } finally {
      nextPageLoading.current = false;
    }
  };

  return { queue, scope, setFilters, search, setSearch, page, setPage, goToNextPage, items, counts, total, loading, error, reload };
}
